// GPS Debug Utility for Attendance System
// Usage: Open browser console and run window.testGPS()

// Haversine formula to calculate distance between two coordinates
const calculateDistance = (lat1, lon1, lat2, lon2) => {
 const R = 6371e3; // metres
 const φ1 = lat1 * Math.PI/180; // φ, λ in radians
 const φ2 = lat2 * Math.PI/180;
 const Δφ = (lat2-lat1) * Math.PI/180;
 const Δλ = (lon2-lon1) * Math.PI/180;

 const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
 Math.cos(φ1) * Math.cos(φ2) *
 Math.sin(Δλ/2) * Math.sin(Δλ/2);
 const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

 return R * c; // in metres
};

// Company locations (default)
const DEFAULT_COMPANY_LOCATIONS = [
 {
 name: 'Văn phòng chính',
 latitude: 10.7628,
 longitude: 106.6602,
 allowedRadius: 300
 }
];

// GPS Debug Function
window.testGPS = async () => {
 console.log('[GPS DEBUG] Starting GPS location test...');
 
 try {
 // Test if geolocation is supported
 if (!navigator.geolocation) {
 console.error('[GPS DEBUG] Geolocation not supported by this browser');
 return;
 }

 // Get current position
 const position = await new Promise((resolve, reject) => {
 navigator.geolocation.getCurrentPosition(
 (pos) => resolve(pos),
 (error) => reject(error),
 {
 enableHighAccuracy: true,
 timeout: 15000,
 maximumAge: 0
 }
 );
 });

 const currentLat = position.coords.latitude;
 const currentLon = position.coords.longitude;
 const accuracy = position.coords.accuracy;

 console.log('[GPS DEBUG] Your current location:');
 console.log(`Latitude: ${currentLat}`);
 console.log(`Longitude: ${currentLon}`);
 console.log(`Accuracy: ±${accuracy}m`);
 console.log(`Timestamp: ${new Date(position.timestamp).toLocaleString()}`);

 // Calculate distances to company locations
 console.log('\n[GPS DEBUG] Distance to company locations:');
 DEFAULT_COMPANY_LOCATIONS.forEach((location, index) => {
 const distance = calculateDistance(currentLat, currentLon, location.latitude, location.longitude);
 const isWithinRange = distance <= location.allowedRadius;
 
 console.log(` ${index + 1}. ${location.name}:`);
 console.log(`Company coordinates: ${location.latitude}, ${location.longitude}`);
 console.log(`Distance: ${Math.round(distance)}m`);
 console.log(`Allowed radius: ${location.allowedRadius}m`);
 console.log(`Within range: ${isWithinRange ? 'YES' : 'NO'}`);
 
 if (!isWithinRange) {
 console.log(`You need to be ${Math.round(distance - location.allowedRadius)}m closer`);
 }
 });

 // Test attendance API call
 console.log('\n[GPS DEBUG] Testing attendance API...');
 try {
 const token = localStorage.getItem('token');
 if (!token) {
 console.warn('[GPS DEBUG] No authentication token found. Please login first.');
 return;
 }

 const response = await fetch('/api/company-locations/info', {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 }
 });

 if (response.ok) {
 const data = await response.json();
 console.log('[GPS DEBUG] Company locations API response:', data);
 } else {
 console.error('[GPS DEBUG] API Error:', response.status, response.statusText);
 const errorText = await response.text();
 console.error('Error details:', errorText);
 }
 } catch (apiError) {
 console.error('[GPS DEBUG] API call failed:', apiError);
 }

 // Generate summary
 console.log('\n [GPS DEBUG] Summary:');
 console.log(`Current location: ${currentLat.toFixed(6)}, ${currentLon.toFixed(6)}`);
 console.log(`GPS accuracy: ±${accuracy}m (${accuracy <= 20 ? 'Good' : accuracy <= 50 ? 'Fair' : 'Poor'})`);
 const nearestDistance = Math.min(...DEFAULT_COMPANY_LOCATIONS.map(loc => 
 calculateDistance(currentLat, currentLon, loc.latitude, loc.longitude)
 ));
 console.log(`Distance to nearest office: ${Math.round(nearestDistance)}m`);
 console.log(`Attendance allowed: ${nearestDistance <= 300 ? 'YES' : 'NO'}`);

 // Return data for further use
 return {
 currentLocation: { lat: currentLat, lon: currentLon, accuracy },
 companyLocations: DEFAULT_COMPANY_LOCATIONS,
 distances: DEFAULT_COMPANY_LOCATIONS.map(loc => ({
 name: loc.name,
 distance: Math.round(calculateDistance(currentLat, currentLon, loc.latitude, loc.longitude)),
 allowedRadius: loc.allowedRadius,
 isWithinRange: calculateDistance(currentLat, currentLon, loc.latitude, loc.longitude) <= loc.allowedRadius
 }))
 };

 } catch (error) {
 console.error('[GPS DEBUG] Error:', error.message);
 
 if (error.code) {
 switch (error.code) {
 case 1:
 console.error('Permission denied. Please allow location access.');
 break;
 case 2:
 console.error('Position unavailable. GPS signal may be weak.');
 break;
 case 3:
 console.error('Timeout. GPS signal may be weak or blocked.');
 break;
 default:
 console.error('Unknown GPS error.');
 }
 }
 }
};

// Calculate distance between two points (utility function)
window.calculateGPSDistance = (lat1, lon1, lat2, lon2) => {
 return Math.round(calculateDistance(lat1, lon1, lat2, lon2));
};

// Check if current location is valid for attendance
window.checkAttendanceLocation = async () => {
 try {
 const position = await new Promise((resolve, reject) => {
 navigator.geolocation.getCurrentPosition(resolve, reject, {
 enableHighAccuracy: true,
 timeout: 10000,
 maximumAge: 0
 });
 });

 const lat = position.coords.latitude;
 const lon = position.coords.longitude;
 
 const distance = calculateDistance(lat, lon, 10.7628, 106.6602);
 const isValid = distance <= 300;
 
 console.log(`Current location: ${lat}, ${lon}`);
 console.log(`Office location: 10.7628, 106.6602`);
 console.log(`Distance: ${Math.round(distance)}m`);
 console.log(`Valid for attendance: ${isValid ? 'YES' : 'NO'}`);
 
 return { lat, lon, distance: Math.round(distance), isValid };
 } catch (error) {
 console.error('Error checking location:', error);
 return null;
 }
};

// Check configured company locations from server
window.checkConfiguredLocations = async () => {
 try {
 console.log('[CONFIG CHECK] Fetching configured company locations...');
 
 const token = localStorage.getItem('token');
 if (!token) {
 console.error('[CONFIG CHECK] No authentication token found. Please login first.');
 return null;
 }

 // Get current GPS position
 const position = await new Promise((resolve, reject) => {
 navigator.geolocation.getCurrentPosition(resolve, reject, {
 enableHighAccuracy: true,
 timeout: 10000,
 maximumAge: 0
 });
 });

 const currentLat = position.coords.latitude;
 const currentLon = position.coords.longitude;
 console.log(`[CONFIG CHECK] Your location: ${currentLat}, ${currentLon}`);

 // Try to get full location data (for admins)
 const fullLocResponse = await fetch('/api/company-locations', {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 }
 });

 if (fullLocResponse.ok) {
 const locations = await fullLocResponse.json();
 console.log('[CONFIG CHECK] Configured locations found:', locations);
 
 if (Array.isArray(locations) && locations.length > 0) {
 console.log('\n[CONFIG CHECK] Distance calculations:');
 
 let validLocations = 0;
 locations.forEach((location, index) => {
 const distance = calculateDistance(currentLat, currentLon, location.latitude, location.longitude);
 const isWithinRange = distance <= location.allowedRadius;
 
 console.log(` ${index + 1}. ${location.name}:`);
 console.log(`Coordinates: ${location.latitude}, ${location.longitude}`);
 console.log(`Distance: ${Math.round(distance)}m`);
 console.log(`Allowed radius: ${location.allowedRadius}m`);
 console.log(`Valid: ${isWithinRange ? 'YES' : 'NO'}`);
 console.log(`Created: ${new Date(location.createdAt).toLocaleString()}`);
 
 if (isWithinRange) validLocations++;
 
 if (!isWithinRange) {
 console.log(`You need to be ${Math.round(distance - location.allowedRadius)}m closer`);
 }
 });
 
 console.log(`\n [CONFIG CHECK] Summary:`);
 console.log(`Total configured locations: ${locations.length}`);
 console.log(`Valid locations for your position: ${validLocations}`);
 console.log(`Can check-in: ${validLocations > 0 ? 'YES' : 'NO'}`);
 
 return {
 currentLocation: { lat: currentLat, lon: currentLon },
 configuredLocations: locations,
 validCount: validLocations,
 canCheckIn: validLocations > 0
 };
 } else {
 console.warn('[CONFIG CHECK] No locations configured in database!');
 console.log('This means system will create default location on first check-in attempt.');
 return { noLocations: true };
 }
 } else {
 console.error('[CONFIG CHECK] Cannot access location data:', fullLocResponse.status);
 console.log('You might not have admin permissions to view full location details.');
 
 // Try the info endpoint as fallback
 const infoResponse = await fetch('/api/company-locations/info', {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
 }
 });
 
 if (infoResponse.ok) {
 const infoData = await infoResponse.json();
 console.log('ℹ[CONFIG CHECK] Basic location info:', infoData);
 return { limitedInfo: infoData };
 }
 }
 } catch (error) {
 console.error('[CONFIG CHECK] Error:', error);
 return null;
 }
};

console.log('[GPS DEBUG] GPS Debug utility loaded!');
console.log('Available commands:');
console.log(' window.testGPS() - Full GPS test and location validation');
console.log(' window.checkAttendanceLocation() - Quick attendance location check');
console.log(' window.checkConfiguredLocations() - Kiểm tra địa điểm đã cấu hình từ server');
console.log(' window.calculateGPSDistance(lat1, lon1, lat2, lon2) - Calculate distance between coordinates');